<script setup lang="js">
import axios from "axios";
import { computed, defineProps, getCurrentInstance, onMounted, ref, watch } from "vue";
import { Formatter } from "@/utilities/Formatter";

const props = defineProps({
  start: {
    type: String,
    required: false,
    default: null
  },
  end: {
    type: String,
    required: false,
    default: null
  },
  resultData: {
    type: Object,
    required: false,
    default: null
  },
  loading: {
    type: Boolean,
    required: false,
    default: false
  },
  error: {
    type: Boolean,
    required: false,
    default: false
  }
});

const standardResult = ref(null);
const tailoringResult = ref(null);
const localLoading = ref(false);
const localError = ref(false);
const { proxy } = getCurrentInstance();
const t = (key) => proxy.$filters.localize(key);

const isLoading = computed(() => props.loading || localLoading.value);
const hasError = computed(() => props.error || localError.value);
const hasData = computed(() =>
  Boolean(props.resultData && standardResult.value && tailoringResult.value)
);

const rows = computed(() => {
  if (!hasData.value) {
    return [];
  }

  return [
    {
      key: "standard",
      title: t("OrderPage.fulfillmentKind.standard"),
      data: standardResult.value
    },
    {
      key: "tailoring",
      title: t("OrderPage.fulfillmentKind.tailoring"),
      data: tailoringResult.value
    }
  ];
});

const load = async () => {
  if (!props.start && !props.end) {
    return;
  }

  try {
    localError.value = false;
    localLoading.value = true;

    const baseUrl = `${window.location.origin}/ui/v3/dashboard`;
    const query = `?start=${props.start}&end=${props.end ?? ""}`;
    const [standard, tailoring] = await Promise.all([
      axios.get(`${baseUrl}/standard${query}`),
      axios.get(`${baseUrl}/tailoring${query}`)
    ]);

    standardResult.value = standard.data;
    tailoringResult.value = tailoring.data;
  } catch {
    localError.value = true;
  } finally {
    localLoading.value = false;
  }
};

onMounted(load);
watch(() => props.start, load);
watch(() => props.end, load);
</script>

<template>
  <section class="kinds-overview">
    <div class="kinds-overview__summary">
      <div class="kinds-overview__summary-text">
        <h2 class="kinds-overview__title">{{ $filters.localize("Dashboard.fulfillmentKind.title") }}</h2>
        <p class="kinds-overview__description">
          {{ $filters.localize("Dashboard.fulfillmentKind.text") }}
        </p>
      </div>
    </div>

    <div v-if="hasData" class="kinds-overview__content">
      <div class="kinds-overview__table-wrap">
        <table class="kinds-overview__table">
          <thead>
            <tr>
              <th>{{ $filters.localize("Dashboard.fulfillmentKind.metaLabel") }}</th>
              <th>{{ $filters.localize("Dashboard.table.orders") }}</th>
              <th>{{ $filters.localize("Dashboard.metrics.turnover") }}</th>
              <th>{{ $filters.localize("Dashboard.table.received") }}</th>
              <th>{{ $filters.localize("Dashboard.metrics.purchase") }}</th>
              <th>{{ $filters.localize("Dashboard.metrics.profit") }}</th>
              <th>{{ $filters.localize("Dashboard.metrics.averageCheckFull") }}</th>
              <th>{{ $filters.localize("Dashboard.metrics.averageMarginShort") }}</th>
              <th>{{ $filters.localize("Dashboard.metrics.marginPercent") }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="row in rows" :key="row.key">
              <td>{{ row.title }}</td>
              <td>{{ row.data.count }}</td>
              <td>{{ Formatter.toCurrency(row.data.turnoverTotal) }}</td>
              <td>{{ Formatter.toCurrency(row.data.turnoverRetailDrop) }}</td>
              <td>{{ Formatter.toCurrency(row.data.purchaseAmount) }}</td>
              <td>{{ Formatter.toCurrency(row.data.profitTotal) }}</td>
              <td>{{ Formatter.toCurrency(row.data.averageCheck) }}</td>
              <td>{{ Formatter.toCurrency(row.data.averageMargin) }}</td>
              <td>{{ Number(row.data.marginPercent ?? 0).toFixed(2) }}%</td>
            </tr>
          </tbody>
          <tfoot>
            <tr class="kinds-overview__total-row">
              <td>{{ $filters.localize("Dashboard.table.total") }}</td>
              <td>{{ props.resultData.count }}</td>
              <td>{{ Formatter.toCurrency(props.resultData.turnoverTotal) }}</td>
              <td>{{ Formatter.toCurrency(props.resultData.turnoverRetailDrop) }}</td>
              <td>{{ Formatter.toCurrency(props.resultData.purchaseAmount) }}</td>
              <td>{{ Formatter.toCurrency(props.resultData.profitTotal) }}</td>
              <td>{{ Formatter.toCurrency(props.resultData.averageCheck) }}</td>
              <td>{{ Formatter.toCurrency(props.resultData.averageMargin) }}</td>
              <td>{{ Number(props.resultData.marginPercent ?? 0).toFixed(2) }}%</td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>

    <div v-else-if="hasError" class="dashboard-state dashboard-state--error">
      {{ $filters.localize("Dashboard.states.overviewError") }}
    </div>

    <div v-else class="kinds-overview__content">
      <div class="dashboard-skeleton__summary">
        <div class="dashboard-skeleton__line dashboard-skeleton__line--title"></div>
        <div class="dashboard-skeleton__line dashboard-skeleton__line--text"></div>
      </div>
      <div class="dashboard-skeleton__table">
        <div v-for="index in 3" :key="`row-${index}`" class="dashboard-skeleton__row"></div>
      </div>
    </div>

    <div v-if="isLoading && hasData" class="dashboard-loader-overlay">
      <div class="dashboard-loader"></div>
    </div>
  </section>
</template>

<style scoped>
.kinds-overview {
  position: relative;
  padding: 1.5rem;
  border: 1px solid rgba(23, 50, 77, 0.08);
  border-radius: 28px;
  background: rgba(255, 255, 255, 0.85);
  box-shadow: 0 22px 60px rgba(23, 50, 77, 0.08);
  overflow: hidden;
}

.kinds-overview__summary {
  display: flex;
  justify-content: space-between;
  gap: 1.25rem;
  align-items: flex-start;
  margin-bottom: 1.25rem;
}

.kinds-overview__summary-text {
  max-width: 52rem;
}

.kinds-overview__title {
  margin: 0;
  color: #17324d;
  font-size: 1.55rem;
  line-height: 1.15;
}

.kinds-overview__description {
  margin: 0.45rem 0 0;
  color: #6f8194;
  font-size: 0.95rem;
  line-height: 1.5;
}

.kinds-overview__content {
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
}

.kinds-overview__table-wrap {
  overflow-x: auto;
}

.kinds-overview__table {
  width: 100%;
  min-width: 920px;
  border-collapse: separate;
  border-spacing: 0;
}

.kinds-overview__table th,
.kinds-overview__table td {
  padding: 0.95rem 0.85rem;
  border-bottom: 1px solid rgba(23, 50, 77, 0.08);
  text-align: left;
  white-space: nowrap;
}

.kinds-overview__table th {
  color: #6f8194;
  font-size: 0.78rem;
  text-transform: uppercase;
  letter-spacing: 0.08em;
}

.kinds-overview__table td {
  color: #17324d;
  font-weight: 500;
}

.kinds-overview__total-row td {
  border-bottom: 0;
  border-top: 2px solid rgba(23, 50, 77, 0.12);
  background: #f5f9fd;
  color: #17324d;
  font-weight: 700;
}

.kinds-overview__total-row td:first-child {
  color: #0f2d49;
}

.dashboard-state {
  min-height: 320px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1.25rem;
  border-radius: 22px;
  text-align: center;
  font-weight: 600;
}

.dashboard-state--error {
  border: 1px dashed rgba(138, 91, 91, 0.24);
  background: rgba(255, 246, 246, 0.92);
  color: #8a5b5b;
}

.dashboard-skeleton__summary,
.dashboard-skeleton__table,
.dashboard-skeleton__line,
.dashboard-skeleton__row {
  display: block;
}

.dashboard-skeleton__line,
.dashboard-skeleton__row {
  background: linear-gradient(90deg, #edf4fb 0%, #f8fbfe 50%, #edf4fb 100%);
  background-size: 200% 100%;
  animation: dashboard-shimmer 1.5s infinite linear;
  border: 1px solid rgba(23, 50, 77, 0.06);
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.55);
}

.dashboard-skeleton__line {
  height: 14px;
  border-radius: 999px;
}

.dashboard-skeleton__line--title {
  width: 34%;
  height: 24px;
}

.dashboard-skeleton__line--text {
  width: 52%;
  margin-top: 0.85rem;
}

.dashboard-skeleton__table {
  display: grid;
  gap: 0.65rem;
}

.dashboard-skeleton__row {
  height: 56px;
  border-radius: 18px;
}

.dashboard-loader-overlay {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(247, 250, 252, 0.55);
  backdrop-filter: blur(2px);
}

.dashboard-loader {
  width: 40px;
  height: 40px;
  border: 4px solid rgba(23, 50, 77, 0.12);
  border-top-color: #17324d;
  border-radius: 50%;
  animation: dashboard-spin 0.8s linear infinite;
}

@keyframes dashboard-spin {
  to {
    transform: rotate(360deg);
  }
}

@keyframes dashboard-shimmer {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
}

@media (max-width: 991.98px) {
  .kinds-overview {
    padding: 1.2rem;
    border-radius: 24px;
  }
}
</style>
